import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface NetworkConfig {
  name: string;
  chainId: number;
  rpcUrl: string;
  explorerUrl: string;
  explorerApiUrl?: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  isTestnet: boolean;
  gasMultiplier: number;
}

export interface BlockchainNetwork {
  network: string;
  config: NetworkConfig;
}

export interface TokenizableAsset {
  id: string;
  name: string;
  symbol: string;
  description?: string;
  category: string;
  totalSupply: string;
  circulatingSupply: string;
  unitPrice: string;
  totalValue: string;
  contractAddress?: string;
  network?: string;
  tokenStandard?: string;
  status: string;
  yieldRate?: number;
  yieldFrequency?: string;
  maturityDate?: string;
  mintedAt?: string;
  createdAt: string;
}

export interface DeployTokenParams {
  assetId: string;
  network: string;
  tokenStandard: string;
}

export interface DeploymentResult {
  success: boolean;
  contractAddress?: string;
  txHash?: string;
  network: string;
  error?: string;
}

export interface SupplyInfo {
  assetId: string;
  totalSupply: string;
  circulatingSupply: string;
  maxSupply?: string;
  mintedTotal: string;
  burnedTotal: string;
  lastMintAt?: string;
  lastBurnAt?: string;
}

// Hook for blockchain networks
export function useBlockchainNetworks() {
  return useQuery({
    queryKey: ['blockchain-networks'],
    queryFn: async () => {
      const response = await api.get<BlockchainNetwork[]>('/tokenization/tokens/networks/all');
      return response.data;
    },
  });
}

export function useMainnetNetworks() {
  return useQuery({
    queryKey: ['blockchain-networks', 'mainnets'],
    queryFn: async () => {
      const response = await api.get<BlockchainNetwork[]>('/tokenization/tokens/networks/mainnets');
      return response.data;
    },
  });
}

export function useTestnetNetworks() {
  return useQuery({
    queryKey: ['blockchain-networks', 'testnets'],
    queryFn: async () => {
      const response = await api.get<BlockchainNetwork[]>('/tokenization/tokens/networks/testnets');
      return response.data;
    },
  });
}

// Hook for tokens
export function useTokens(filters?: { status?: string; category?: string; network?: string }) {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.category) params.append('category', filters.category);
  if (filters?.network) params.append('network', filters.network);

  return useQuery({
    queryKey: ['tokens', filters],
    queryFn: async () => {
      const response = await api.get<TokenizableAsset[]>(`/tokenization/tokens?${params}`);
      return response.data;
    },
  });
}

export function useTokenInfo(assetId: string) {
  return useQuery({
    queryKey: ['token', assetId],
    queryFn: async () => {
      const response = await api.get<TokenizableAsset>(`/tokenization/tokens/${assetId}`);
      return response.data;
    },
    enabled: !!assetId,
  });
}

export function useSupplyInfo(assetId: string) {
  return useQuery({
    queryKey: ['token-supply', assetId],
    queryFn: async () => {
      const response = await api.get<SupplyInfo>(`/tokenization/tokens/${assetId}/supply`);
      return response.data;
    },
    enabled: !!assetId,
  });
}

// Mutations
export function useDeployToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeployTokenParams) => {
      const response = await api.post<DeploymentResult>('/tokenization/tokens/deploy', params);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
      queryClient.invalidateQueries({ queryKey: ['token', variables.assetId] });
    },
  });
}

export function useMintTokens() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { assetId: string; amount: number; reason: string; toAddress?: string }) => {
      const response = await api.post(`/tokenization/tokens/${params.assetId}/mint`, params);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['token-supply', variables.assetId] });
      queryClient.invalidateQueries({ queryKey: ['token', variables.assetId] });
    },
  });
}

export function useBurnTokens() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { assetId: string; amount: number; reason: string; fromAddress?: string }) => {
      const response = await api.post(`/tokenization/tokens/${params.assetId}/burn`, params);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['token-supply', variables.assetId] });
      queryClient.invalidateQueries({ queryKey: ['token', variables.assetId] });
    },
  });
}

export function usePauseToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assetId: string) => {
      const response = await api.put(`/tokenization/tokens/${assetId}/pause`);
      return response.data;
    },
    onSuccess: (_, assetId) => {
      queryClient.invalidateQueries({ queryKey: ['token', assetId] });
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });
}

export function useUnpauseToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assetId: string) => {
      const response = await api.put(`/tokenization/tokens/${assetId}/unpause`);
      return response.data;
    },
    onSuccess: (_, assetId) => {
      queryClient.invalidateQueries({ queryKey: ['token', assetId] });
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });
}

// Helper to get explorer URLs
export function getExplorerTxUrl(network: string, txHash: string, networks: BlockchainNetwork[]): string {
  const networkConfig = networks.find(n => n.network === network);
  if (!networkConfig?.config.explorerUrl) return '';
  return `${networkConfig.config.explorerUrl}/tx/${txHash}`;
}

export function getExplorerAddressUrl(network: string, address: string, networks: BlockchainNetwork[]): string {
  const networkConfig = networks.find(n => n.network === network);
  if (!networkConfig?.config.explorerUrl) return '';
  return `${networkConfig.config.explorerUrl}/address/${address}`;
}

export function getExplorerTokenUrl(network: string, contractAddress: string, networks: BlockchainNetwork[]): string {
  const networkConfig = networks.find(n => n.network === network);
  if (!networkConfig?.config.explorerUrl) return '';
  return `${networkConfig.config.explorerUrl}/token/${contractAddress}`;
}


// ============================================
// TENANT CATEGORIES
// ============================================

export interface TenantCategory {
  id: string;
  tenantId: string;
  categoryId: string;
  categoryName: string;
  category: string;
  enabled: boolean;
  maxEmission?: string;
  currentEmission: string;
  enabledAt?: string;
  disabledAt?: string;
}

export function useEnabledCategories() {
  return useQuery({
    queryKey: ['tenant-categories', 'enabled'],
    queryFn: async () => {
      const response = await api.get<TenantCategory[]>('/tokenization/tenant-categories/enabled');
      return response.data;
    },
  });
}

export function useAllTenantCategories(tenantId?: string) {
  const params = new URLSearchParams();
  if (tenantId) params.append('tenantId', tenantId);

  return useQuery({
    queryKey: ['tenant-categories', 'all', tenantId],
    queryFn: async () => {
      const response = await api.get<TenantCategory[]>(`/tokenization/tenant-categories?${params}`);
      return response.data;
    },
  });
}

export function useEnableCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { tenantId: string; categoryId: string; maxEmission?: number }) => {
      const response = await api.post('/tokenization/tenant-categories', params);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-categories'] });
    },
  });
}

export function useDisableCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { tenantId: string; categoryId: string }) => {
      await api.delete(`/tokenization/tenant-categories/${params.tenantId}/${params.categoryId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-categories'] });
    },
  });
}

export function useUpdateCategoryEmission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { tenantId: string; categoryId: string; maxEmission?: number }) => {
      const response = await api.put(
        `/tokenization/tenant-categories/${params.tenantId}/${params.categoryId}`,
        { maxEmission: params.maxEmission }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-categories'] });
    },
  });
}

// ============================================
// COMPLIANCE
// ============================================

export interface ComplianceStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  documentsRequested: number;
  byCategory: Record<string, number>;
  avgReviewTime: number;
}

export interface PendingAsset {
  id: string;
  name: string;
  symbol: string;
  category: string;
  totalValue: string;
  status: string;
  createdAt: string;
  tenant?: { id: string; name: string };
  documents?: any[];
}

export function useComplianceStats() {
  return useQuery({
    queryKey: ['compliance-stats'],
    queryFn: async () => {
      const response = await api.get<ComplianceStats>('/tokenization/compliance/stats');
      return response.data;
    },
  });
}

export function usePendingAssets(filters?: { category?: string; priority?: string; search?: string }) {
  const params = new URLSearchParams();
  if (filters?.category) params.append('category', filters.category);
  if (filters?.priority) params.append('priority', filters.priority);
  if (filters?.search) params.append('search', filters.search);

  return useQuery({
    queryKey: ['compliance-pending', filters],
    queryFn: async () => {
      const response = await api.get<PendingAsset[]>(`/tokenization/compliance/pending?${params}`);
      return response.data;
    },
  });
}

export function useApproveAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { assetId: string; notes?: string }) => {
      const response = await api.post(`/tokenization/compliance/review/${params.assetId}/approve`, {
        notes: params.notes,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-pending'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-stats'] });
    },
  });
}

export function useRejectAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { assetId: string; reason: string }) => {
      const response = await api.post(`/tokenization/compliance/review/${params.assetId}/reject`, {
        reason: params.reason,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-pending'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-stats'] });
    },
  });
}

export function useRequestDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { assetId: string; requirements: string[]; notes?: string }) => {
      const response = await api.post(`/tokenization/compliance/review/${params.assetId}/request-documents`, {
        requirements: params.requirements,
        notes: params.notes,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-pending'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-stats'] });
    },
  });
}
